import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Loader2, Wallet, CheckCircle2, AlertTriangle,
  CreditCard, Calendar, Clock, MapPin, ShieldCheck, Sparkles,
} from 'lucide-react';
import { useCustomerAuth } from '../App';
import { customerV2Api, customerApi } from '../lib/api';
import GuestCheckoutForm from '../components/customer/GuestCheckoutForm';

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};
const formatTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};


// ─── Checkout flow:
//      pending → reserving → reserved → paying → success → redirect /tickets/:token
//                                     ↘ failure (show error + restore)
const CheckoutPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { customer, loading: authLoading, loginCustomer } = useCustomerAuth();

  const [phase, setPhase] = useState('idle'); // idle | reserving | reserved | paying | success | failed
  const [bookingDraft, setBookingDraft] = useState(null); // from sessionStorage
  const [createdBooking, setCreatedBooking] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmount, setWalletAmount] = useState(0);
  const [error, setError] = useState(null);

  // 1) Hydrate draft from sessionStorage (set by EventDetailPage)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('v2_pending_booking');
      if (!raw) {
        navigate(`/events/${slug}`, { replace: true });
        return;
      }
      const draft = JSON.parse(raw);
      // Sanity: must match this slug
      if (draft.event_slug !== slug) {
        sessionStorage.removeItem('v2_pending_booking');
        navigate(`/events/${slug}`, { replace: true });
        return;
      }
      // Expiry: 30 minutes old draft is stale
      if (Date.now() - (draft.created_at || 0) > 30 * 60 * 1000) {
        sessionStorage.removeItem('v2_pending_booking');
        navigate(`/events/${slug}`, { replace: true });
        return;
      }
      setBookingDraft(draft);
    } catch {
      navigate(`/events/${slug}`, { replace: true });
    }
  }, [slug, navigate]);

  // 2) Auth gate REMOVED — Phase 10: guest checkout.
  // If no customer is signed in, the GuestCheckoutForm renders inline below
  // and handles auto-signup via OTP. No redirect to /account/login.

  // 3) Fetch wallet balance
  useEffect(() => {
    if (!customer) return;
    (async () => {
      try {
        const res = await customerApi.getWalletBalance();
        setWalletBalance(res.data || { available: 0 });
      } catch { setWalletBalance({ available: 0 }); }
    })();
  }, [customer]);

  // 4) Derived: maximum wallet we can apply
  const maxWallet = Math.min(walletBalance?.available || 0, bookingDraft?.total || 0);
  const finalWalletApplied = useWallet ? Math.max(0, Math.min(walletAmount, maxWallet)) : 0;
  const gatewayAmount = Math.max(0, (bookingDraft?.total || 0) - finalWalletApplied);

  // ─── Step 1: create booking (reserve inventory) ─────────────────────
  const createBooking = async () => {
    if (!bookingDraft) return;
    setPhase('reserving');
    setError(null);
    try {
      const res = await customerV2Api.createBooking({
        event_id: bookingDraft.event_id,
        items: bookingDraft.items.map((i) => ({
          pass_type_id: i.pass_type_id,
          quantity: i.quantity,
        })),
        wallet_amount: finalWalletApplied,
      });
      setCreatedBooking(res.data);
      setPhase('reserved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = 'Could not reserve passes. Please try again.';
      if (detail?.code === 'SOLD_OUT') msg = detail.message || 'One or more passes just sold out.';
      else if (detail?.message) msg = detail.message;
      setError(msg);
      setPhase('failed');
    }
  };

  // ─── Step 2: trigger payment ────────────────────────────────────────
  // The backend response from /checkout indicates which gateway to use:
  //   • { gateway: 'paytm',       gateway_url, form_fields, ... }      → auto-POST to Paytm
  //   • { gateway: 'razorpay',    order_id, amount, key_id, ... }       → open Razorpay drop-in
  //   • { gateway: 'wallet_only', skip_checkout: true, booking }        → fully paid via wallet
  const [checkoutInit, setCheckoutInit] = useState(null);

  const beginPayment = async () => {
    if (!createdBooking) return;
    setError(null);
    setPhase('paying');
    try {
      const res = await customerV2Api.createCheckout(createdBooking._id);
      const init = res.data;
      setCheckoutInit(init);

      if (init.skip_checkout) {
        // Wallet covers the full amount — already finalized
        sessionStorage.removeItem('v2_pending_booking');
        const tok = init.booking?.public_ticket_token;
        if (tok) {
          setPhase('success');
          setTimeout(() => navigate(`/tickets/${tok}`, { replace: true }), 900);
        } else if (init.booking?.booking_status === 'awaiting_approval') {
          setPhase('success_vip');
          setCreatedBooking(init.booking);
        }
        return;
      }

      if (init.gateway === 'razorpay') {
        await openRazorpayCheckout(init, createdBooking._id);
      } else if (init.gateway === 'paytm') {
        // Auto-POST to Paytm and leave our domain. The Paytm webhook will
        // post the result back to /api/v2/payments/webhook/paytm and
        // redirect the browser to /checkout/payment-status.
        sessionStorage.removeItem('v2_pending_booking');
        autoPostPaytmForm(init);
      } else {
        setError(`Unsupported payment gateway: ${init.gateway || 'unknown'}`);
        setPhase('failed');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not start payment';
      setError(typeof msg === 'string' ? msg : 'Could not start payment');
      setPhase('failed');
    }
  };

  // ─── Paytm classic redirect — auto-POST hidden form ──────────────────
  const autoPostPaytmForm = (init) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = init.gateway_url;
    form.style.display = 'none';
    Object.entries(init.form_fields || {}).forEach(([k, v]) => {
      const inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = k;
      inp.value = v;
      form.appendChild(inp);
    });
    document.body.appendChild(form);
    // Slight delay so the user sees the "Redirecting…" state momentarily
    setTimeout(() => form.submit(), 600);
  };

  // Razorpay drop-in
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const openRazorpayCheckout = async (init, bookingId) => {
    const ok = await loadRazorpayScript();
    if (!ok) {
      setError("Couldn't load payment gateway. Please check your network.");
      setPhase('failed');
      return;
    }
    const rzp = new window.Razorpay({
      key:      init.key_id,
      order_id: init.order_id,
      amount:   init.amount,
      currency: init.currency || 'INR',
      name:     'Scratch Unleashed',
      description: bookingDraft.event_title,
      prefill: {
        name:  customer?.full_name || '',
        contact: customer?.phone || '',
        email: customer?.email || '',
      },
      notes: { booking_id: bookingId },
      theme: { color: '#D4A843' },
      modal: {
        ondismiss: () => {
          setPhase('reserved');
        },
      },
      handler: async (response) => {
        setPhase('paying');
        try {
          // Server-side verify — backend cross-checks signature + amount.
          const verifyRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/payments/verify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('customerToken')}`,
            },
            body: JSON.stringify({
              booking_id:          bookingId,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          });
          const updated = await verifyRes.json();
          if (!verifyRes.ok) {
            throw new Error(updated?.detail || 'Verification failed');
          }
          if (updated.payment_status === 'paid' && updated.public_ticket_token) {
            sessionStorage.removeItem('v2_pending_booking');
            setPhase('success');
            setTimeout(() => navigate(`/tickets/${updated.public_ticket_token}`, { replace: true }), 900);
          } else if (updated.booking_status === 'awaiting_approval') {
            sessionStorage.removeItem('v2_pending_booking');
            setPhase('success_vip');
            setCreatedBooking(updated);
          } else {
            setPhase('failed');
            setError('Payment verification incomplete. Please contact support.');
          }
        } catch (e) {
          setPhase('failed');
          setError(e?.message || 'Payment verification failed.');
        }
      },
    });
    rzp.on('payment.failed', (resp) => {
      setPhase('failed');
      setError(resp?.error?.description || 'Payment failed. Inventory has been released.');
    });
    rzp.open();
  };
  const triggerMockPayment = async () => {
    // Mock simulator removed — events now route through Paytm or Razorpay.
    setError('Mock payment is no longer available. Please retry the booking.');
    setPhase('failed');
  };



  // ─── Render ─────────────────────────────────────────────────────────
  if (authLoading || !bookingDraft) {
    return (
      <div className="events-loading">
        <Loader2 size={26} className="animate-spin text-[var(--accent-gold)]" />
        <p>Preparing checkout…</p>
      </div>
    );
  }

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <Helmet>
        <title>{`Checkout · ${bookingDraft.event_title} · Scratch Unleashed`}</title>
      </Helmet>

      <header className="checkout-header">
        <Link to={`/events/${slug}`} className="event-back-btn" data-testid="checkout-back">
          <ArrowLeft size={16} /> Back to event
        </Link>
        <h1 className="checkout-title">Checkout</h1>
      </header>

      <main className="checkout-main">
        {/* ── Order summary ─────────────────────────────────── */}
        <section className="checkout-card" data-testid="checkout-summary">
          <h3 className="checkout-section-title">{bookingDraft.event_title}</h3>
          <div className="checkout-event-meta">
            <span><Calendar size={12} className="text-[var(--accent-gold)]" /> {formatDate(bookingDraft.event_date)}</span>
            {bookingDraft.event_time && <span><Clock size={12} className="text-[var(--accent-gold)]" /> {formatTime(bookingDraft.event_time)}</span>}
            {bookingDraft.venue && <span><MapPin size={12} className="text-[var(--accent-gold)]" /> {bookingDraft.venue}</span>}
          </div>

          <ul className="checkout-items" data-testid="checkout-items">
            {bookingDraft.items.map((it, idx) => (
              <li key={idx} className="checkout-item" data-testid={`checkout-item-${idx}`}>
                <span>
                  <span className="text-zinc-200">{it.name}</span>
                  <span className="text-zinc-500 ml-2 text-sm">× {it.quantity}</span>
                </span>
                <span className="text-zinc-300 font-mono">{fmtINR(it.price * it.quantity)}</span>
              </li>
            ))}
          </ul>

          <div className="checkout-totals">
            <div className="checkout-total-row">
              <span className="text-zinc-400">Subtotal</span>
              <span className="text-zinc-200">{fmtINR(bookingDraft.total)}</span>
            </div>
            {finalWalletApplied > 0 && (
              <div className="checkout-total-row" data-testid="checkout-wallet-applied">
                <span className="text-emerald-400 inline-flex items-center gap-1">
                  <Wallet size={12} /> Wallet
                </span>
                <span className="text-emerald-400 font-mono">− {fmtINR(finalWalletApplied)}</span>
              </div>
            )}
            <div className="checkout-total-row checkout-total-grand">
              <span>To pay</span>
              <span data-testid="checkout-to-pay">{fmtINR(gatewayAmount)}</span>
            </div>
          </div>
        </section>

        {/* ── Wallet toggle ─────────────────────────────────── */}
        {walletBalance && walletBalance.available > 0 && phase !== 'success' && phase !== 'success_vip' && (
          <section className="checkout-card" data-testid="checkout-wallet-card">
            <header className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet size={16} className="text-[var(--accent-gold)]" />
                <span className="text-zinc-200 font-medium">Use Scratch Points</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = !useWallet;
                  setUseWallet(next);
                  setWalletAmount(next ? Math.min(walletBalance.available, bookingDraft.total) : 0);
                }}
                disabled={phase !== 'idle' && phase !== 'failed'}
                className={`wallet-toggle ${useWallet ? 'wallet-toggle-on' : ''}`}
                data-testid="checkout-wallet-toggle"
                aria-pressed={useWallet}
              >
                <span className="wallet-toggle-dot" />
              </button>
            </header>
            <p className="text-xs text-zinc-500 mt-2">
              Available: <span className="text-zinc-300 font-mono">{fmtINR(walletBalance.available)}</span>
              {' '}· 1 point = ₹1
            </p>

            {useWallet && (
              <div className="mt-3">
                <input
                  type="range"
                  min={0}
                  max={maxWallet}
                  value={walletAmount}
                  onChange={(e) => setWalletAmount(parseInt(e.target.value, 10) || 0)}
                  disabled={phase !== 'idle' && phase !== 'failed'}
                  className="wallet-slider"
                  data-testid="checkout-wallet-slider"
                />
                <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-2">
                  <span>Apply: <span className="text-zinc-200 font-mono">{fmtINR(walletAmount)}</span></span>
                  <button
                    type="button"
                    className="text-[var(--accent-gold)] hover:underline"
                    onClick={() => setWalletAmount(maxWallet)}
                    data-testid="checkout-wallet-max"
                  >
                    Use max
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Phase 10 — Guest checkout form (only when not signed in) ── */}
        {!authLoading && !customer && phase === 'idle' && bookingDraft && (
          <GuestCheckoutForm
            bookingDraft={bookingDraft}
            walletAmount={finalWalletApplied}
            onSuccess={({ token, customer: cust, booking }) => {
              // Persist session via AuthContext (mirrors normal login flow)
              loginCustomer(token, cust);
              // Skip the separate "Reserve" step — booking is already created
              setCreatedBooking(booking);
              setPhase('reserved');
            }}
          />
        )}

        {/* ── Step 1: Reserve (only when already signed in) ───────────── */}
        {customer && phase === 'idle' && (
          <button
            type="button"
            className="checkout-action-btn"
            onClick={createBooking}
            data-testid="checkout-reserve-btn"
          >
            <ShieldCheck size={16} />
            Reserve & Continue
          </button>
        )}

        {phase === 'reserving' && (
          <div className="checkout-status" data-testid="checkout-reserving">
            <Loader2 size={20} className="animate-spin text-[var(--accent-gold)]" />
            <span className="text-zinc-300">Reserving your passes…</span>
          </div>
        )}

        {/* ── Step 2: Pay via configured gateway (Paytm/Razorpay) ─────── */}
        {phase === 'reserved' && createdBooking && (
          <section className="checkout-card checkout-pay-card" data-testid="checkout-pay-card">
            <header className="checkout-pay-header">
              <CreditCard size={18} className="text-[var(--accent-gold)]" />
              <h3 className="text-zinc-100 font-semibold">Complete Payment</h3>
            </header>
            <p className="text-sm text-zinc-400 mb-4">
              {gatewayAmount > 0
                ? <>You'll be charged <span className="text-zinc-200 font-mono">{fmtINR(gatewayAmount)}</span>.</>
                : <>Your wallet covers the full amount.</>}
            </p>

            <div className="checkout-pay-actions">
              <button
                type="button"
                className="checkout-pay-success"
                onClick={beginPayment}
                data-testid="checkout-pay-now"
              >
                <CreditCard size={16} /> Pay {fmtINR(gatewayAmount)}
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 text-center">
              Secured payment · UPI · Cards · Net Banking · Wallets
            </p>

            <p className="text-[11px] text-zinc-500 mt-3 text-center" data-testid="checkout-reservation-hint">
              Reserved for 10 minutes. Inventory is released if not paid in time.
            </p>
          </section>
        )}

        {phase === 'paying' && (
          <div className="checkout-status" data-testid="checkout-paying">
            <Loader2 size={20} className="animate-spin text-[var(--accent-gold)]" />
            <span className="text-zinc-300">Processing payment…</span>
          </div>
        )}

        {phase === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="checkout-success"
            data-testid="checkout-success"
          >
            <CheckCircle2 size={42} className="text-emerald-400 mb-3" />
            <h3 className="text-white text-xl font-semibold mb-1">Booking Confirmed</h3>
            <p className="text-zinc-400 text-sm">Redirecting to your ticket…</p>
          </motion.div>
        )}

        {phase === 'success_vip' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="checkout-success"
            data-testid="checkout-success-vip"
          >
            <Sparkles size={42} className="text-[var(--accent-gold)] mb-3" />
            <h3 className="text-white text-xl font-semibold mb-2">Payment received — awaiting approval</h3>
            <p className="text-zinc-400 text-sm max-w-sm text-center mb-5">
              Your booking includes a VIP pass that requires admin approval. We'll notify you within 24 hours; if rejected, the amount is refunded to your wallet.
            </p>
            <Link to="/account/bookings" className="btn-gold" data-testid="checkout-view-bookings">
              View My Bookings
            </Link>
          </motion.div>
        )}

        <AnimatePresence>
          {phase === 'failed' && error && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="checkout-error"
              data-testid="checkout-error"
            >
              <AlertTriangle size={18} className="text-red-400 shrink-0" />
              <div className="flex-1">
                <p className="text-red-300 text-sm font-medium">{error}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    className="btn-gold-outline"
                    onClick={() => { setPhase('idle'); setError(null); setCreatedBooking(null); }}
                    data-testid="checkout-retry-btn"
                  >
                    Try again
                  </button>
                  <Link to={`/events/${slug}`} className="btn-gold-outline">Back to event</Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default CheckoutPage;
